import SectionLabel from '../SectionLabel'

// Parse an itinerary altitude string ("5,106m", "730m", "—") to metres, or null.
function parseAlt(s) {
  if (!s) return null
  const digits = String(s).replace(/[^0-9]/g, '')
  return digits ? parseInt(digits, 10) : null
}

// Elevation profile drawn from the real trek.itinerary altitudes. No altitude is
// hardcoded — days without a numeric altitude (e.g. a buffer "—" day) are skipped.
export default function TrekAltitudeProfile({ trek }) {
  const raw = (trek?.itinerary || []).map(d => ({ day: d.day, title: d.title, m: parseAlt(d.altitude) }))
  const pts = raw.filter(p => p.m != null)
  const skipped = raw.filter(p => p.m == null)

  if (pts.length < 2) return null   // nothing meaningful to plot

  // Chart geometry (SVG user units; scales responsively via viewBox)
  const W = 820, H = 320, padL = 58, padR = 24, padT = 28, padB = 52
  const days = pts.map(p => p.day)
  const minDay = Math.min(...days), maxDay = Math.max(...days)
  const maxAlt = Math.max(...pts.map(p => p.m))
  const yTop = Math.ceil((maxAlt + 200) / 1000) * 1000   // round up to a clean gridline
  const x = day => padL + ((day - minDay) / (maxDay - minDay || 1)) * (W - padL - padR)
  const y = m => padT + (1 - m / yTop) * (H - padT - padB)

  const line = pts.map(p => `${x(p.day).toFixed(1)},${y(p.m).toFixed(1)}`).join(' ')
  const area = `${padL},${y(0)} ${line} ${x(maxDay).toFixed(1)},${y(0)}`
  const gridlines = Array.from({ length: yTop / 1000 + 1 }, (_, i) => i * 1000)

  const peak = pts.reduce((a, b) => (b.m > a.m ? b : a), pts[0])
  const start = pts[0], end = pts[pts.length - 1]
  const accl = pts.find(p => /acclimat/i.test(p.title))
  // Key x-axis stops (dedup by day)
  const keyStops = [start, accl, peak, end].filter(Boolean)
    .filter((p, i, arr) => arr.findIndex(q => q.day === p.day) === i)

  const shortTitle = t => (t.split(/[–—-]/)[0].split('(')[0].trim().slice(0, 16))

  return (
    <div style={{ marginBottom: '48px' }}>
      <SectionLabel>Topographic</SectionLabel>
      <h2 className="section-title" style={{ fontSize: '24px', marginBottom: '20px' }}>Altitude profile.</h2>

      <div style={{ width: '100%', overflowX: 'auto' }}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block', minWidth: '520px' }}
             role="img" aria-label={`Altitude profile peaking at ${peak.m.toLocaleString()} metres`}>
          {/* Horizontal map-rule gridlines + mono altitude labels */}
          {gridlines.map(g => (
            <g key={g}>
              <line x1={padL} y1={y(g)} x2={W - padR} y2={y(g)} stroke="var(--color-rule)" strokeWidth="1" />
              <text x={padL - 10} y={y(g) + 3} textAnchor="end"
                    fontFamily="var(--font-mono)" fontSize="10" fill="var(--color-text-secondary)">
                {g.toLocaleString()}
              </text>
            </g>
          ))}

          {/* Area + line */}
          <polygon points={area} fill="var(--color-accent-cool)" opacity="0.10" />
          <polyline points={line} fill="none" stroke="var(--color-accent-cool)" strokeWidth="2"
                    strokeLinejoin="round" strokeLinecap="round" />

          {/* Day dots */}
          {pts.map(p => (
            <circle key={p.day} cx={x(p.day)} cy={y(p.m)} r="2.5" fill="var(--color-accent-cool)" />
          ))}

          {/* High point marker — the pass, called out in mono */}
          <circle cx={x(peak.day)} cy={y(peak.m)} r="5" fill="none" stroke="var(--color-text)" strokeWidth="1.5" />
          <line x1={x(peak.day)} y1={y(peak.m) - 8} x2={x(peak.day)} y2={y(peak.m) - 26}
                stroke="var(--color-text)" strokeWidth="1" />
          <text x={x(peak.day)} y={y(peak.m) - 32} textAnchor="middle"
                fontFamily="var(--font-mono)" fontSize="12" fontWeight="700" fill="var(--color-text)">
            {peak.m.toLocaleString()} m
          </text>
          <text x={x(peak.day)} y={y(peak.m) - 46} textAnchor="middle"
                fontFamily="var(--font-heading)" fontSize="11" letterSpacing="1" fill="var(--color-text)"
                style={{ textTransform: 'uppercase' }}>
            {shortTitle(peak.title)}
          </text>

          {/* X-axis baseline + key stop labels */}
          <line x1={padL} y1={y(0)} x2={W - padR} y2={y(0)} stroke="var(--color-text)" strokeWidth="1" />
          {keyStops.map(p => (
            <g key={p.day}>
              <line x1={x(p.day)} y1={y(0)} x2={x(p.day)} y2={y(0) + 5} stroke="var(--color-text)" strokeWidth="1" />
              <text x={x(p.day)} y={y(0) + 18} textAnchor="middle"
                    fontFamily="var(--font-mono)" fontSize="10" fill="var(--color-text)">
                DAY {p.day}
              </text>
              <text x={x(p.day)} y={y(0) + 32} textAnchor="middle"
                    fontSize="10" fill="var(--color-text-secondary)">
                {shortTitle(p.title)}
              </text>
            </g>
          ))}
        </svg>
      </div>

      <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '10px' }}>
        Plotted from {pts.length} days with recorded altitude.
        {skipped.length > 0 && ` ${skipped.length} day${skipped.length > 1 ? 's' : ''} without an altitude (e.g. drive/buffer) not shown.`}
      </div>
    </div>
  )
}
